/**
 * Portais de licitação e contas em portais (17 §3.1, 11 §A).
 * A conta nunca guarda senha (CLAUDE.md, regra 10). Qualquer usuário cria e altera; só o admin apaga (RLS).
 * O alerta de renovação por e-mail entra no módulo de alertas; aqui a lista mostra os dias para vencer.
 */
import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  Edit,
  FunctionField,
  List,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  SimpleList,
  TextField,
  TextInput,
  UrlField,
  DateInput,
  AutocompleteInput,
  regex,
  required,
  useRecordContext,
} from "react-admin";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import { AcoesLista, BarraFormulario, CampoData, useCelular, vazioParaNulo } from "../componentes";
import { diasAte, formatarData } from "../lib/formato";

/** Antecedência do alerta de assinatura de portal (17 §3.3: 10 dias). */
const ANTECEDENCIA_ALERTA_DIAS = 10;

const validarUrl = regex(/^https?:\/\/\S+$/, "Informe o endereço completo, começando com https://");

/* --------------------------------- Portais --------------------------------- */

export function PortalLista() {
  const celular = useCelular();
  return (
    <List title="Portais" sort={{ field: "nome", order: "ASC" }} actions={<AcoesLista />} exporter={false}>
      {celular ? (
        <SimpleList
          primaryText={(r) => r.nome}
          secondaryText={(r) => r.url ?? "Sem endereço"}
          tertiaryText={(r) => (r.ativo ? "" : "Inativo")}
          linkType="edit"
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick="edit">
          <TextField source="nome" />
          <UrlField source="url" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} emptyText="—" />
          <BooleanField source="ativo" />
        </Datagrid>
      )}
    </List>
  );
}

function FormularioPortal() {
  return (
    <SimpleForm toolbar={<BarraFormulario />}>
      <TextInput source="nome" validate={required()} fullWidth />
      <TextInput source="url" parse={vazioParaNulo} validate={validarUrl} fullWidth helperText="Ex.: https://..." />
      <BooleanInput source="ativo" defaultValue={true} helperText="Inativo some das listas de escolha, mas o histórico continua." />
    </SimpleForm>
  );
}

export function PortalCriar() {
  return (
    <Create title="Novo portal" redirect="list">
      <FormularioPortal />
    </Create>
  );
}

export function PortalEditar() {
  return (
    <Edit title="Editar portal" mutationMode="pessimistic" redirect="list">
      <FormularioPortal />
    </Edit>
  );
}

/* ---------------------------- Contas em portais ---------------------------- */

interface ContaPortal {
  id: string;
  vencimento_assinatura: string | null;
  ativo: boolean;
}

/** Situação da assinatura: vencida, vencendo (dentro da antecedência do alerta) ou em dia. */
export function situacaoAssinatura(vencimento: string | null, hoje = new Date()) {
  if (!vencimento) return { rotulo: "Sem vencimento", cor: "default" as const };
  const dias = diasAte(vencimento, hoje);
  if (dias < 0) return { rotulo: `Vencida há ${-dias} dia${dias === -1 ? "" : "s"}`, cor: "error" as const };
  if (dias === 0) return { rotulo: "Vence hoje", cor: "error" as const };
  if (dias <= ANTECEDENCIA_ALERTA_DIAS) return { rotulo: `Vence em ${dias} dia${dias === 1 ? "" : "s"}`, cor: "warning" as const };
  return { rotulo: `${dias} dias`, cor: "default" as const };
}

function CampoSituacao() {
  const registro = useRecordContext<ContaPortal>();
  if (!registro) return null;
  if (!registro.ativo) return <Chip size="small" label="Inativa" variant="outlined" />;
  const { rotulo, cor } = situacaoAssinatura(registro.vencimento_assinatura);
  return <Chip size="small" label={rotulo} color={cor} variant={cor === "default" ? "outlined" : "filled"} />;
}

const filtrosConta = [
  <ReferenceInput key="portal" source="portal_id" reference="portal" alwaysOn>
    <SelectInput optionText="nome" label="Portal" emptyText="Todos os portais" />
  </ReferenceInput>,
  <SelectInput
    key="ativo"
    source="ativo"
    label="Situação"
    choices={[
      { id: true, name: "Ativas" },
      { id: false, name: "Inativas" },
    ]}
    alwaysOn
  />,
];

export function ContaPortalLista() {
  const celular = useCelular();
  return (
    <List
      title="Contas em portais"
      sort={{ field: "vencimento_assinatura", order: "ASC" }}
      filters={filtrosConta}
      filterDefaultValues={{ ativo: true }}
      actions={<AcoesLista />}
      exporter={false}
    >
      {celular ? (
        <SimpleList
          primaryText={(r) => r.usuario_login}
          secondaryText={(r) => (
            <>
              <ReferenceField record={r} source="portal_id" reference="portal" link={false}>
                <TextField source="nome" variant="body2" />
              </ReferenceField>
              {` · vence ${formatarData(r.vencimento_assinatura)}`}
            </>
          )}
          tertiaryText={() => <CampoSituacao />}
          linkType="edit"
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick="edit">
          <ReferenceField source="portal_id" reference="portal" link={false}>
            <TextField source="nome" />
          </ReferenceField>
          <TextField source="usuario_login" />
          <TextField source="plano" emptyText="—" />
          <CampoData source="vencimento_assinatura" />
          <FunctionField label="Situação" render={() => <CampoSituacao />} />
          <ReferenceField source="responsavel_id" reference="usuario" link={false} emptyText="—">
            <TextField source="nome" />
          </ReferenceField>
        </Datagrid>
      )}
    </List>
  );
}

function FormularioConta() {
  return (
    <SimpleForm toolbar={<BarraFormulario />}>
      <Alert severity="warning" sx={{ mb: 1, width: "100%" }}>
        Não guarde a senha aqui. O sistema registra só o login, o plano e o vencimento.
      </Alert>
      <ReferenceInput source="portal_id" reference="portal" filter={{ ativo: true }} sort={{ field: "nome", order: "ASC" }}>
        <SelectInput optionText="nome" validate={required()} fullWidth />
      </ReferenceInput>
      <TextInput source="usuario_login" validate={required()} fullWidth />
      <TextInput source="plano" parse={vazioParaNulo} fullWidth />
      <DateInput source="vencimento_assinatura" parse={vazioParaNulo} />
      <ReferenceInput source="responsavel_id" reference="usuario" filter={{ ativo: true }} sort={{ field: "nome", order: "ASC" }}>
        <AutocompleteInput optionText="nome" parse={vazioParaNulo} fullWidth />
      </ReferenceInput>
      <BooleanInput source="ativo" defaultValue={true} />
    </SimpleForm>
  );
}

export function ContaPortalCriar() {
  return (
    <Create title="Nova conta em portal" redirect="list">
      <FormularioConta />
    </Create>
  );
}

export function ContaPortalEditar() {
  return (
    <Edit title="Editar conta em portal" mutationMode="pessimistic" redirect="list">
      <FormularioConta />
    </Edit>
  );
}
